import React, {
  createContext,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Snackbar,
} from "@mui/material";

const AlertContext = createContext(null);

export const AlertProvider = ({ children }) => {
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showAlert = (message, severity = "success") => {
    setAlert({
      open: true,
      message,
      severity,
    });
  };

  const showSuccess = (message) => {
    showAlert(message, "success");
  };

  const showError = (message) => {
    showAlert(message, "error");
  };

  const showWarning = (message) => {
    showAlert(message, "warning");
  };

  const showInfo = (message) => {
    showAlert(message, "info");
  };

  const handleClose = (_, reason) => {
    if (reason === "clickaway") return;

    setAlert((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const value = useMemo(
    () => ({
      showAlert,
      showSuccess,
      showError,
      showWarning,
      showInfo,
    }),
    []
  );

  return (
    <AlertContext.Provider value={value}>
      {children}

      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          mt: 1,
          mr: 1,
        }}
      >
        <Alert
          onClose={handleClose}
          severity={alert.severity}
          variant="standard"
          sx={{
            minWidth: {
              xs: 280,
              sm: 340,
            },
            borderRadius: 2,
            fontSize: 14,
            fontWeight: 500,
            boxShadow: "none",
            alignItems: "center",
            border: "1px solid",
            "& .MuiAlert-icon": {
              fontSize: 20,
            },

            ...(alert.severity === "success" && {
              bgcolor: "#EDF7ED",
              color: "#1E4620",
              borderColor: "#C8E6C9",
            }),

            ...(alert.severity === "error" && {
              bgcolor: "#FDEDED",
              color: "#5F2120",
              borderColor: "#F5C2C7",
            }),

            ...(alert.severity === "warning" && {
              bgcolor: "#FFF4E5",
              color: "#663C00",
              borderColor: "#FFE0B2",
            }),

            ...(alert.severity === "info" && {
              bgcolor: "#E5F6FD",
              color: "#014361",
              borderColor: "#B3E5FC",
            }),
          }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </AlertContext.Provider>
  );
};

export const useAlert = () => {
  return useContext(AlertContext);
};